import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowOutward } from '@mui/icons-material';
import TeamLogo from '../../components/TeamLogo';
import Loading from '../../components/Loading';
import { mapMappings } from '../../Constants';
import { RoundEnd } from '../../models/RoundEnd';
import { Team } from '../../models/Team';
import { LiveMatch, LiveTeam, currentRound, useLiveMatch } from '../../services/LiveService';
import Match2Rounds from '../MatchPage2/Match2Rounds';
import LiveBadge from './LiveBadge';
import LiveScoreboard from './LiveScoreboard';
import pageStyles from '../MatchPage2/MatchPage2.module.css';
import styles from './Live.module.css';

// Ids for the round strip when the API couldn't resolve real team ids: Match2Rounds only
// compares the winner id against team1's, so any two distinct values work.
const SYNTHETIC_TEAM1 = 'live-team-1';
const SYNTHETIC_TEAM2 = 'live-team-2';

// /live/:matchId — one map's live scoreboard: MatchPage2's header with the running score, the
// round strip, and both teams' boards. Everything re-renders from the socket; nothing polls.
const LiveMatchPage: React.FC = () => {
    const { matchId } = useParams<{ matchId: string }>();
    const { t } = useTranslation('pages');
    const { match, board, loaded, connected } = useLiveMatch(matchId);

    const team1Id = match?.team1.teamId ?? SYNTHETIC_TEAM1;
    const team2Id = match?.team2.teamId ?? SYNTHETIC_TEAM2;

    // The feed's round list → the demo parser's RoundEnd shape that Match2Rounds draws.
    const roundEnds = useMemo(() => {
        const ends: { [round: number]: RoundEnd } = {};
        for (const r of board?.rounds ?? []) {
            ends[r.round] = new RoundEnd(r.reason, r.winner === 1 ? team1Id : team2Id);
        }
        return ends;
    }, [board, team1Id, team2Id]);

    const back = (
        <div className={styles.topRow}>
            <Link to={`/matches?tab=${match?.finished ? 'results' : 'live'}`} className={styles.backLink}>← {t('live.backToMatches')}</Link>
            {/* The not-live state carries its own link in the body. */}
            {match && <Link to={`/prematch/${matchId}`} className={styles.link}>{t('live.toMatchPage')} <ArrowOutward sx={{ fontSize: 13 }} className={styles.outIcon} /></Link>}
        </div>
    );

    if (!loaded) {
        return <div className={pageStyles.page}>{back}<Loading /></div>;
    }

    if (!match) {
        return (
            <div className={pageStyles.page}>
                {back}
                <div className={styles.empty}>
                    <div className={styles.emptyTitle}>{t('live.notLiveTitle')}</div>
                    <p>{t('live.notLiveText')}</p>
                    <Link to={`/prematch/${matchId}`} className={styles.link}>{t('live.toMatchPage')} →</Link>
                </div>
            </div>
        );
    }

    const roundTeam1 = toTeam(match.team1, team1Id, match);

    return (
        <div className={pageStyles.page}>
            {back}
            <LiveHeader match={match} />
            {!connected && <div className={styles.notice}>{t('live.reconnecting')}</div>}
            {match.finished && <div className={styles.notice}>{t('live.finishedText')}</div>}

            <div className={styles.boards}>
                <LiveScoreboard team={match.team1} players={board?.team1Players ?? []} />
                <Match2Rounds
                    roundEnds={roundEnds}
                    team1={roundTeam1}
                    team2Id={team2Id}
                    padToRegulation
                    currentRound={match.finished ? undefined : currentRound(match)}
                />
                <LiveScoreboard team={match.team2} players={board?.team2Players ?? []} />
            </div>

            <div className={styles.note}>
                <span>{t('live.ratingNote')}</span>
                <span>
                    {t('live.source')}{' '}
                    <a href="https://liga.dust2.dk/" target="_blank" rel="noopener noreferrer" className={styles.link}>
                        POWER LIGAEN <ArrowOutward sx={{ fontSize: 13 }} className={styles.outIcon} />
                    </a>
                </span>
            </div>
        </div>
    );
};

// Plain div when the team couldn't be matched to one of ours, so there's no dead link. Module-level
// so live re-renders don't remount the logos.
const TeamSide: React.FC<{ team: LiveTeam; className: string; children: React.ReactNode }> =
    ({ team, className, children }) => team.teamId
        ? <Link to={`/team/${team.teamId}`} className={className}>{children}</Link>
        : <div className={className}>{children}</div>;

// MatchPage2's header geometry with the running score and a LIVE line in the centre panel.
const LiveHeader: React.FC<{ match: LiveMatch }> = ({ match }) => {
    const { t } = useTranslation('pages');
    const { team1, team2 } = match;
    const map = match.map ? mapMappings.get(match.map) ?? match.map.replace(/^de_/, '') : null;
    const meta = [
        match.divisionName,
        match.season != null ? t('common:seasonNum', { number: match.season }) : null,
    ].filter(Boolean).join(' · ');
    const format = [
        map,
        t('live.round', { round: currentRound(match) }),
        match.bestOf > 1 ? t('live.series', { a: match.seriesTeam1, b: match.seriesTeam2 }) : null,
    ].filter(Boolean).join(' · ');

    return (
        <div className={pageStyles.header}>
            <TeamSide team={team1} className={pageStyles.headerTeam}>
                <TeamLogo logo={team1.logo} name={team1.name} teamId={team1.teamId} className={pageStyles.headerLogo} />
                <span className={pageStyles.headerTeamName}>{team1.name}</span>
            </TeamSide>
            <div className={pageStyles.headerCenter}>
                <div className={pageStyles.headerAccent} />
                <div className={pageStyles.headerMeta}>{meta}</div>
                <div className={pageStyles.headerScore}>
                    <span className={team1.score > team2.score ? pageStyles.scoreWin : undefined}>{team1.score}</span>
                    <span className={pageStyles.scoreColon}>:</span>
                    <span className={team2.score > team1.score ? pageStyles.scoreWin : undefined}>{team2.score}</span>
                </div>
                <div className={`${pageStyles.headerFormat} ${styles.headerStatus}`}>
                    <LiveBadge finished={match.finished} />
                    <span>{format}</span>
                </div>
            </div>
            <TeamSide team={team2} className={`${pageStyles.headerTeam} ${pageStyles.headerTeamRight}`}>
                <span className={pageStyles.headerTeamName}>{team2.name}</span>
                <TeamLogo logo={team2.logo} name={team2.name} teamId={team2.teamId} className={pageStyles.headerLogo} />
            </TeamSide>
        </div>
    );
};

function toTeam(team: LiveTeam, teamId: string, match: LiveMatch): Team {
    return new Team(teamId, '', match.season ?? 0, match.division, team.name, team.logo ?? '', '', '', '');
}

export default LiveMatchPage;
